/**
 * Transport (spec 48) and comparison controls (spec 16, 39, 90-95).
 *
 * The transport is deliberately persistent and deliberately independent of
 * hardware: nothing on this bar is affected by which DAC, amplifier or
 * transducer is selected, which is the visible half of the promise that
 * changing hardware does not touch the source clock.
 */

import { useApp } from '../../stores/appStore';
import { Segmented, fmtTime } from './primitives';

export function Transport() {
  const playing = useApp((s) => s.playing);
  const position = useApp((s) => s.position);
  const duration = useApp((s) => s.duration);
  const looping = useApp((s) => s.looping);
  const buffer = useApp((s) => s.buffer);
  const meters = useApp((s) => s.meters);
  const engineSettings = useApp((s) => s.project.engine);
  const comparison = useApp((s) => s.project.comparison);
  const activeSystem = useApp((s) => s.activeSystem);
  const blind = useApp((s) => s.blind);
  const abxTruth = useApp((s) => s.abxTruth);
  const abxTrials = useApp((s) => s.abxTrials);
  const levelMatchResult = useApp((s) => s.levelMatchResult);
  const levelMatching = useApp((s) => s.levelMatching);
  const banks = useApp((s) => s.project.banks);
  const activeBank = useApp((s) => s.activeBank);

  const togglePlay = useApp((s) => s.togglePlay);
  const stop = useApp((s) => s.stop);
  const seek = useApp((s) => s.seek);
  const setLoop = useApp((s) => s.setLoop);
  const toggleMute = useApp((s) => s.toggleMute);
  const setMasterGain = useApp((s) => s.setMasterGain);
  const setComparison = useApp((s) => s.setComparison);
  const toggleAb = useApp((s) => s.toggleAb);
  const startAbx = useApp((s) => s.startAbx);
  const answerAbx = useApp((s) => s.answerAbx);
  const selectBank = useApp((s) => s.selectBank);

  const pct = duration > 0 ? (position / duration) * 100 : 0;
  const correct = abxTrials.filter((t) => t.correct).length;

  return (
    <div className="transport">
      <div className="row" style={{ gap: 4 }}>
        <button className="btn icon" onClick={stop} disabled={!buffer} title="Stop" aria-label="Stop">
          ■
        </button>
        <button
          className={`btn icon${playing ? ' on' : ''}`}
          onClick={togglePlay}
          disabled={!buffer}
          title="Play / pause (Space)"
          aria-label={playing ? 'Pause' : 'Play'}
          style={{ width: 40 }}
        >
          {playing ? '❚❚' : '▶'}
        </button>
        <button
          className={`btn icon${looping ? ' on' : ''}`}
          onClick={() => setLoop(!looping)}
          disabled={!buffer}
          title="Loop"
          aria-label="Loop"
        >
          ↻
        </button>
      </div>

      <div className="time">
        {fmtTime(position)} <small>/ {fmtTime(duration)}</small>
      </div>

      <div className="scrub">
        <div className="scrub-track">
          <div className="scrub-fill" style={{ width: `${pct}%` }} />
        </div>
        <input
          type="range"
          min={0}
          max={Math.max(0.001, duration)}
          step={0.001}
          value={position}
          onChange={(e) => seek(Number(e.target.value))}
          disabled={!buffer}
          aria-label="Playback position"
        />
      </div>

      <div className="meter-pair" title="Output level, post-limiter">
        <div className="meter">
          <div className="fill" style={{ width: `${meterPct(meters?.peakL)}%` }} />
          <div className="peak" style={{ left: `${meterPct(meters?.peakHoldL)}%` }} />
        </div>
        <div className="meter">
          <div className="fill" style={{ width: `${meterPct(meters?.peakR)}%` }} />
          <div className="peak" style={{ left: `${meterPct(meters?.peakHoldR)}%` }} />
        </div>
      </div>

      <div className="row" style={{ gap: 4 }}>
        <button
          className={`btn icon${engineSettings.muted ? ' on' : ''}`}
          onClick={toggleMute}
          title="Mute (M)"
          aria-label="Mute"
        >
          {engineSettings.muted ? '🔇' : '🔊'}
        </button>
        <input
          type="range"
          min={-60}
          max={0}
          step={0.5}
          value={engineSettings.masterGainDb}
          onChange={(e) => setMasterGain(Number(e.target.value))}
          style={{ width: 84 }}
          aria-label="Master gain"
          title={`Master ${engineSettings.masterGainDb.toFixed(1)} dB`}
        />
        <span className="mono tiny dim" style={{ width: 42, textAlign: 'right' }}>
          {engineSettings.masterGainDb.toFixed(1)}
        </span>
      </div>

      <div style={{ width: 1, height: 30, background: 'var(--line)' }} />

      <div className="row" style={{ gap: 6 }}>
        <Segmented
          small
          value={comparison}
          onChange={setComparison}
          options={[
            { value: 'single', label: 'Single', title: 'One system' },
            { value: 'ab', label: 'A/B', title: 'Two complete systems, switch between them' },
            { value: 'abx', label: 'ABX', title: 'Blind ABX with randomised X' },
            { value: 'bank', label: 'Bank', title: 'Four-way rapid switching with keys 1-4' },
          ]}
          ariaLabel="Comparison mode"
        />

        {comparison === 'ab' && (
          <button className="btn sm" onClick={toggleAb} title="Switch systems (A)">
            {blind ? '?' : activeSystem} → {blind ? '?' : activeSystem === 'A' ? 'B' : 'A'}
          </button>
        )}

        {comparison === 'abx' && (
          <>
            {abxTruth === null ? (
              <button className="btn sm primary" onClick={startAbx}>
                New trial
              </button>
            ) : (
              <>
                <span className="pill blind">X playing</span>
                <button className="btn sm" onClick={() => answerAbx('A', 3)}>
                  X = A
                </button>
                <button className="btn sm" onClick={() => answerAbx('B', 3)}>
                  X = B
                </button>
              </>
            )}
            {abxTrials.length > 0 && (
              <span className="mono tiny dim" title="Correct identifications out of trials completed">
                {correct}/{abxTrials.length}
              </span>
            )}
          </>
        )}

        {comparison === 'bank' && (
          <div className="row" style={{ gap: 2 }}>
            {[0, 1, 2, 3].map((i) => {
              const id = banks.dac[i];
              return (
                <button
                  key={i}
                  className={`btn sm${activeBank.dac === i ? ' on' : ''}`}
                  disabled={!id}
                  onClick={() => selectBank('dac', i)}
                  title={id ? `Bank ${i + 1}` : `Bank ${i + 1} is empty — assign hardware from the library`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
        )}

        {levelMatching ? (
          <span className="pill off">matching…</span>
        ) : levelMatchResult && levelMatchResult.mode !== 'off' ? (
          <span
            className="mono tiny"
            style={{ color: 'var(--accent)' }}
            title={levelMatchResult.summary}
          >
            LM {levelMatchResult.trimB >= 0 ? '+' : ''}
            {levelMatchResult.trimB.toFixed(2)} dB
          </span>
        ) : null}
      </div>
    </div>
  );
}

function meterPct(db: number | undefined): number {
  if (db === undefined || !Number.isFinite(db)) return 0;
  // -60 dB to 0 dB across the meter.
  return Math.max(0, Math.min(100, ((db + 60) / 60) * 100));
}
